import { retry, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { isUnauthorized, shouldRetryRequest } from './retryPolicy';

export type AppBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

interface EnhanceBaseQueryOptions {
  baseQuery: AppBaseQuery;
  /** Called once per request whose session has expired — not once per retry attempt. */
  onUnauthorized: () => void;
  /** Overridable so tests do not sit through the exponential backoff. */
  backoff?: (attempt: number, maxRetries: number) => Promise<void>;
}

/** Wraps the base query: retries inside, login redirect outside, so an expired session redirects once. */
export const enhanceBaseQuery = ({ baseQuery, onUnauthorized, backoff }: EnhanceBaseQueryOptions): AppBaseQuery => {
  const withRetries = retry(baseQuery, {
    /**
     * Retry policy lives in `shouldRetryRequest`. `maxRetries` can't be passed together with it, so
     * the per-endpoint value is forwarded to the policy.
     */
    retryCondition: (error, _args, { attempt, baseQueryApi, extraOptions }) =>
      shouldRetryRequest({
        error: error as FetchBaseQueryError,
        attempt,
        requestType: baseQueryApi.type,
        maxRetries: extraOptions?.maxRetries,
      }),
    ...(backoff ? { backoff } : {}),
  });

  return async (args, api, extraOptions) => {
    const result = await withRetries(args, api, extraOptions);

    if (isUnauthorized(result.error as FetchBaseQueryError | undefined)) {
      onUnauthorized();
    }

    return result;
  };
};
